/**
 * useNodePreviewData Hook
 *
 * Fetches output data from the last successful run for all predecessor actions.
 * This data is used to show previews of token values in the node editor.
 */

import { useMemo, useCallback } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import {
  fetchRunsList,
  fetchRunWithOutputLinks,
  fetchActionContent,
  type RunWithOutputLinks,
} from '@/services/monitoringApi';
import { useWorkflowStore } from '@/stores/workflowStore';

// ============================================================================
// Types
// ============================================================================

export interface NodePreviewData {
  /** Whether we have a last successful run */
  hasRunData: boolean;
  /** Whether data is still loading */
  isLoading: boolean;
  /** The run ID if available */
  runId: string | null;
  /** When the last run completed */
  runTime: string | null;
  /** Output data keyed by action name */
  outputs: Record<string, unknown>;
  /** Variable values extracted from InitializeVariable actions, keyed by variable name */
  variables: Record<string, unknown>;
  /** Whether the workflow has changed since the last run (any action is stale) */
  isStale: boolean;
  /** Error message if any */
  error?: string;
}

interface ActionDefinition {
  runAfter?: Record<string, string[]>;
  type?: string;
  kind?: string;
  inputs?: Record<string, unknown>;
}

/**
 * Variable definition structure from InitializeVariable action inputs
 */
interface VariableDefinition {
  name: string;
  type?: string;
  value?: unknown;
}

interface TriggerDefinition {
  type?: string;
  kind?: string;
}

// ============================================================================
// Internal Hook for API Options
// ============================================================================

function useApiOptions() {
  const { workflowName } = useParams<{ workflowName: string }>();
  const { selectedProject, selectedApp } = useAppStore();
  const { getOttoToken, isAuthenticated, isLoading: authLoading } = useAuth();

  // Only ready when we have app info, workflow name, and auth is ready (authenticated and not loading)
  const isReady = !!selectedProject && !!selectedApp && !!workflowName && isAuthenticated && !authLoading;

  const acquireToken = useCallback(async (): Promise<string> => {
    const token = await getOttoToken();
    if (!token) {
      throw new Error('Failed to acquire token');
    }
    return token;
  }, [getOttoToken]);

  return {
    workflowName,
    projectName: selectedProject,
    appName: selectedApp?.name,
    acquireToken,
    isReady,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Extract action name from node ID (strip prefixes like "action-" or "trigger-")
 */
function extractActionName(nodeId: string | null | undefined): string | null {
  if (!nodeId) return null;

  if (nodeId.startsWith('action-')) {
    return nodeId.slice('action-'.length);
  }
  if (nodeId.startsWith('trigger-')) {
    return nodeId.slice('trigger-'.length);
  }

  return nodeId;
}

/**
 * Get all predecessor action names for a given action using the workflow definition.
 * Uses BFS to traverse the runAfter dependency graph backwards.
 */
function getPredecessorActions(
  currentNodeId: string | null | undefined,
  actions: Record<string, ActionDefinition>,
  triggers: Record<string, TriggerDefinition>
): { predecessors: string[]; includesTrigger: boolean } {
  const predecessors: string[] = [];
  let includesTrigger = false;

  const currentActionName = extractActionName(currentNodeId);

  if (!currentActionName) {
    // If no current action, return all actions
    return {
      predecessors: Object.keys(actions),
      includesTrigger: Object.keys(triggers).length > 0,
    };
  }

  const currentAction = actions[currentActionName];
  if (!currentAction) {
    // Action not found - return all actions
    return {
      predecessors: Object.keys(actions),
      includesTrigger: Object.keys(triggers).length > 0,
    };
  }

  // BFS to find all predecessors
  const queue: string[] = [];
  const visited = new Set<string>();

  // Start with direct predecessors from runAfter
  const runAfter = currentAction.runAfter;
  if (runAfter && Object.keys(runAfter).length > 0) {
    Object.keys(runAfter).forEach((name) => {
      if (!visited.has(name)) {
        queue.push(name);
        visited.add(name);
      }
    });
  } else {
    // If no runAfter, this action runs after trigger
    includesTrigger = true;
  }

  // BFS through the dependency graph
  while (queue.length > 0) {
    const actionName = queue.shift()!;
    predecessors.push(actionName);

    const action = actions[actionName];
    if (action?.runAfter && Object.keys(action.runAfter).length > 0) {
      Object.keys(action.runAfter).forEach((predName) => {
        if (!visited.has(predName)) {
          queue.push(predName);
          visited.add(predName);
        }
      });
    } else {
      // Action with no runAfter is connected to trigger
      includesTrigger = true;
    }
  }

  return { predecessors, includesTrigger };
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to get the latest run with all output links (trigger + actions).
 * Uses two-step fetch: first gets runs list, then fetches specific run with $expand
 * to get outputsLink for ALL actions including built-ins.
 */
function useLatestRunWithOutputs() {
  const { workflowName, projectName, appName , acquireToken, isReady } = useApiOptions();

  return useQuery({
    queryKey: ['latestRunWithOutputs', workflowName, projectName, appName],
    queryFn: async (): Promise<RunWithOutputLinks | null> => {
      if (!projectName || !appName || !workflowName) {
        throw new Error('Missing required context');
      }

      const token = await acquireToken();
      const opts = {
        projectName,
        appName,
        workflowName,
        accessToken: token,
      };

      // Step 1: Get runs list - latest run is first in array
      const runs = await fetchRunsList(opts);

      if (runs.length === 0) {
        return null;
      }

      // Get the latest run (first in list)
      const latestRunInfo = runs[0];
      if (!latestRunInfo) {
        return null;
      }

      // Step 2: Fetch that specific run with $expand to get all output links
      const runWithLinks = await fetchRunWithOutputLinks(opts, latestRunInfo.runId);

      return runWithLinks;
    },
    enabled: isReady,
    staleTime: 60000,
    // Always refetch when the designer remounts (e.g., after the user switches
    // from the monitoring tab back to the designer). Without this, a run that
    // completed while the designer was unmounted stays invisible to the input
    // sidebar previews until the workflow is fully refreshed, because React
    // Query serves the previous (pre-run) cached result for the staleTime
    // window. The downstream per-action output queries are keyed by runId, so
    // they'll naturally refetch once a newer run is observed here.
    refetchOnMount: 'always',
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });
}

/**
 * Hook to get predecessor action names for a node
 */
function usePredecessorActions(nodeId: string | null): {
  predecessors: string[];
  includesTrigger: boolean;
  /** Predecessor action names that are InitializeVariable type */
  variableActions: string[];
} {
  const workflow = useWorkflowStore((s) => s.workflow);

  return useMemo(() => {
    if (!nodeId || !workflow?.definition) {
      return { predecessors: [], includesTrigger: false, variableActions: [] };
    }

    const definition = workflow.definition as {
      actions?: Record<string, ActionDefinition>;
      triggers?: Record<string, TriggerDefinition>;
    };

    const actions = definition.actions ?? {};
    const triggers = definition.triggers ?? {};

    const result = getPredecessorActions(nodeId, actions, triggers);

    // Identify which predecessors are InitializeVariable actions
    const variableActions = result.predecessors.filter((actionName) => {
      const actionDef = actions[actionName];
      return actionDef?.type?.toLowerCase() === 'initializevariable';
    });

    return { ...result, variableActions };
  }, [nodeId, workflow]);
}

/**
 * Main hook to get preview data for a node's parameter fields
 */
export function useNodePreviewData(nodeId: string | null): NodePreviewData {
  const { acquireToken, isReady } = useApiOptions();

  // Get the latest run with output links
  const { data: latestRun, isLoading: runLoading, error: runError } = useLatestRunWithOutputs();

  // Get predecessor action names (includes variable action identification from workflow definition)
  const { predecessors, includesTrigger, variableActions } = usePredecessorActions(nodeId);

  // Fetch trigger output directly from the outputsLink
  const triggerOutputQuery = useQuery({
    queryKey: ['triggerOutput', latestRun?.runId, latestRun?.triggerOutputLink?.uri],
    queryFn: async (): Promise<unknown> => {
      if (!latestRun?.triggerOutputLink?.uri) {
        return undefined;
      }
      const token = await acquireToken();
      return fetchActionContent(latestRun.triggerOutputLink, token);
    },
    enabled: isReady && !!latestRun?.runId && includesTrigger && !!latestRun?.triggerOutputLink?.uri,
    staleTime: 300000, // Cache for 5 minutes
  });

  // Fetch outputs for actions using parallel queries
  // Uses outputsLink directly from the expanded run data (works for both built-ins and API connections)
  const actionQueries = useQueries({
    queries: predecessors.map((actionName) => {
      const outputLink = latestRun?.actionOutputLinks?.[actionName];
      return {
        queryKey: ['actionOutput', latestRun?.runId, actionName, outputLink?.uri],
        queryFn: async (): Promise<{ actionName: string; output: unknown }> => {
          if (!outputLink?.uri) {
            // No output link available for this action
            return { actionName, output: undefined };
          }

          const token = await acquireToken();
          const output = await fetchActionContent(outputLink, token);
          return { actionName, output };
        },
        enabled: isReady && !!latestRun?.runId,
        staleTime: 300000, // Cache for 5 minutes
      };
    }),
  });

  // Fetch inputs for InitializeVariable actions to extract variable values.
  // Reads `actionInputLinks` directly from the already-expanded run payload —
  // no need to round-trip through `fetchActionDetails`. That used to do two
  // sequential network calls per variable (action endpoint → input content);
  // we now hit only the input content endpoint.
  const variableInputQueries = useQueries({
    queries: variableActions.map((actionName) => {
      const inputLink = latestRun?.actionInputLinks?.[actionName];
      return {
        queryKey: ['variableInput', latestRun?.runId, actionName, inputLink?.uri],
        queryFn: async (): Promise<{ actionName: string; input: unknown }> => {
          if (!inputLink?.uri) {
            return { actionName, input: undefined };
          }
          const token = await acquireToken();
          const input = await fetchActionContent(inputLink, token);
          return { actionName, input };
        },
        enabled: isReady && !!latestRun?.runId && variableActions.length > 0,
        staleTime: 300000,
      };
    }),
  });

  // Combine all outputs into a single object
  const outputs = useMemo(() => {
    const result: Record<string, unknown> = {};

    // Add trigger output if available
    if (includesTrigger && triggerOutputQuery.data !== undefined) {
      result['trigger'] = triggerOutputQuery.data;
    }

    // Add action outputs
    for (const query of actionQueries) {
      if (query.data) {
        result[query.data.actionName] = query.data.output;
      }
    }

    return result;
  }, [includesTrigger, triggerOutputQuery.data, actionQueries]);

  // Extract variable values from InitializeVariable action inputs
  // Input format: { variables: [{ name: 'varName', type: 'String', value: 'someValue' }] }
  const variables = useMemo(() => {
    const result: Record<string, unknown> = {};

    for (const query of variableInputQueries) {
      if (query.data?.input && typeof query.data.input === 'object') {
        const input = query.data.input as { variables?: VariableDefinition[] };
        const variablesDef = input.variables;

        if (Array.isArray(variablesDef)) {
          for (const varDef of variablesDef) {
            if (varDef.name) {
              result[varDef.name] = varDef.value;
            }
          }
        }
      }
    }

    return result;
  }, [variableInputQueries]);

  // Check if any queries are still loading
  const isLoading = runLoading ||
    triggerOutputQuery.isLoading ||
    actionQueries.some((q) => q.isLoading) ||
    variableInputQueries.some((q) => q.isLoading);

  // Build the result
  return useMemo((): NodePreviewData => {
    if (runError) {
      return {
        hasRunData: false,
        isLoading: false,
        runId: null,
        runTime: null,
        outputs: {},
        variables: {},
        isStale: false,
        error: runError instanceof Error ? runError.message : 'Failed to fetch run data',
      };
    }

    if (!latestRun) {
      return {
        hasRunData: false,
        isLoading: runLoading,
        runId: null,
        runTime: null,
        outputs: {},
        variables: {},
        isStale: false,
      };
    }

    return {
      hasRunData: true,
      isLoading,
      runId: latestRun.runId,
      runTime: latestRun.endTime ?? latestRun.startTime,
      outputs,
      variables,
      isStale: false, // TODO: Compare workflow definition to detect changes
    };
  }, [latestRun, runLoading, runError, isLoading, outputs, variables]);
}

/**
 * Simplified hook that just returns outputs and variables for use in TokenizedInput
 */
export function usePreviewOutputs(nodeId: string | null): {
  outputs: Record<string, unknown>;
  variables: Record<string, unknown>;
  hasRunData: boolean;
  isLoading: boolean;
  isStale: boolean;
} {
  const data = useNodePreviewData(nodeId);

  return {
    outputs: data.outputs,
    variables: data.variables,
    hasRunData: data.hasRunData,
    isLoading: data.isLoading,
    isStale: data.isStale,
  };
}
